import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

// Forma de las sugerencias expuesta al UI. Se conserva el shape snake_case
// heredado de la API legacy para no tocar los componentes que la consumen.
interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface PlaceDetails {
  comuna?: string;
  ciudad?: string;
  codigo_postal?: string;
  formatted_address?: string;
}

interface UseGooglePlacesReturn {
  searchPlaces: (input: string) => void;
  getPlaceDetails: (placeId: string) => Promise<PlaceDetails | null>;
  predictions: PlacePrediction[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
}

// El Loader se comparte a nivel de módulo: si el hook se monta en varios
// componentes, todos reutilizan la misma promesa y el script se pide una vez.
let placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null;

const cargarPlacesLibrary = (
  apiKey: string
): Promise<google.maps.PlacesLibrary> => {
  if (!placesLibraryPromise) {
    const loader = new Loader({
      apiKey,
      version: 'weekly',
      language: 'es',
      region: 'CL',
    });
    placesLibraryPromise = loader.importLibrary('places');
  }
  return placesLibraryPromise;
};

// La API nueva rechaza la promesa en vez de devolver un status code; el
// detalle técnico queda en consola y al usuario le llega algo accionable.
const traducirError = (err: unknown): string => {
  const detalle = err instanceof Error ? err.message : String(err);

  if (/denied|not authorized|referer|api key|blocked/i.test(detalle)) {
    return 'Acceso denegado. Verifica la configuración de la API key.';
  }
  if (/quota|exhausted|OVER_QUERY_LIMIT/i.test(detalle)) {
    return 'Límite de consultas excedido.';
  }
  return 'No se pudo completar la búsqueda. Intenta de nuevo.';
};

export const useGooglePlaces = (): UseGooglePlacesReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const libRef = useRef<google.maps.PlacesLibrary | null>(null);
  // Token de sesión: agrupa todas las búsquedas de un tipeo más el
  // fetchFields final en un único evento facturable. Sin token, Google cobra
  // cada pulsación por separado.
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  // Guardamos las sugerencias crudas por place_id porque toPlace() arrastra el
  // token de sesión; construir el Place desde el id suelto rompe la sesión y
  // el detalle se factura aparte.
  const sugerenciasRef = useRef(
    new Map<string, google.maps.places.PlacePrediction>()
  );
  // El input viene con debounce: descarta respuestas que lleguen fuera de orden.
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

    if (!apiKey) {
      // Sin API key: degradación graciosa. El usuario puede ingresar la
      // dirección manualmente. No exponemos el detalle técnico al UI.
      console.info(
        'ℹ️  Google Places API no configurada — autocompletado deshabilitado, ingreso manual disponible'
      );
      return;
    }

    let cancelado = false;

    cargarPlacesLibrary(apiKey)
      .then((lib) => {
        if (cancelado) return;
        libRef.current = lib;
        sessionTokenRef.current = new lib.AutocompleteSessionToken();
        setIsLoaded(true);
        setError(null);
      })
      .catch((err) => {
        if (cancelado) return;
        console.error('❌ Error cargando Google Places API:', err);
        setError('Error cargando Google Places API. Verifica la configuración.');
      });

    return () => {
      // Solo cortamos el efecto: el script queda cargado para el resto de la
      // app, que es justamente lo que permite compartir la promesa del Loader.
      cancelado = true;
    };
  }, []);

  const searchPlaces = useCallback((input: string) => {
    const lib = libRef.current;
    const termino = input.trim();

    if (!lib || !termino) {
      setPredictions([]);
      setError(null);
      return;
    }

    const seq = ++requestSeqRef.current;
    setIsLoading(true);
    setError(null);

    lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: termino,
      includedRegionCodes: ['cl'],
      // La API nueva no acepta la colección 'address' que usaba la legacy: hay
      // que enumerar los tipos concretos de la Tabla B (máximo 5).
      includedPrimaryTypes: [
        'street_address',
        'route',
        'premise',
        'subpremise',
      ],
      language: 'es',
      region: 'cl',
      sessionToken: sessionTokenRef.current ?? undefined,
    })
      .then(({ suggestions }) => {
        if (seq !== requestSeqRef.current) return; // respuesta obsoleta

        sugerenciasRef.current.clear();
        const resultados: PlacePrediction[] = [];

        for (const sugerencia of suggestions) {
          const prediccion = sugerencia.placePrediction;
          if (!prediccion) continue;

          sugerenciasRef.current.set(prediccion.placeId, prediccion);
          resultados.push({
            place_id: prediccion.placeId,
            description: prediccion.text.text,
            structured_formatting: {
              main_text: prediccion.mainText?.text ?? prediccion.text.text,
              secondary_text: prediccion.secondaryText?.text ?? '',
            },
          });
        }

        setPredictions(resultados);
        setIsLoading(false);
      })
      .catch((err) => {
        if (seq !== requestSeqRef.current) return;

        console.error('❌ Error en el autocompletado de Places:', err);
        setPredictions([]);
        setError(traducirError(err));
        setIsLoading(false);
      });
  }, []);

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<PlaceDetails | null> => {
      const lib = libRef.current;
      if (!lib) return null;

      try {
        const prediccion = sugerenciasRef.current.get(placeId);
        const place = prediccion
          ? prediccion.toPlace()
          : new lib.Place({ id: placeId });

        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress'],
        });

        // fetchFields cierra la sesión de facturación: el token queda quemado
        // y la próxima búsqueda necesita uno nuevo.
        sessionTokenRef.current = new lib.AutocompleteSessionToken();
        sugerenciasRef.current.clear();

        return extraerComponentesDireccion(place);
      } catch (err) {
        console.error('❌ Error obteniendo detalles del lugar:', err);
        return null;
      }
    },
    []
  );

  return {
    searchPlaces,
    getPlaceDetails,
    predictions,
    isLoading,
    isLoaded,
    error,
  };
};

// Extrae los campos de dirección que usa el wizard de clientes. Ojo: la API
// nueva expone longText/shortText, no long_name/short_name como la legacy.
function extraerComponentesDireccion(
  place: google.maps.places.Place
): PlaceDetails {
  const componentes = place.addressComponents ?? [];
  const details: PlaceDetails = {
    formatted_address: place.formattedAddress ?? undefined,
  };

  for (const componente of componentes) {
    const tipos = componente.types;
    const valor = componente.longText ?? undefined;
    if (!valor) continue;

    // Comuna: puede venir como locality o sublocality según el sector.
    if (
      !details.comuna &&
      (tipos.includes('locality') ||
        tipos.includes('sublocality') ||
        tipos.includes('administrative_area_level_3'))
    ) {
      details.comuna = valor;
    }

    // En Chile administrative_area_level_1 es la región.
    if (tipos.includes('administrative_area_level_1')) {
      details.ciudad = valor;
    }

    if (tipos.includes('postal_code')) {
      details.codigo_postal = valor;
    }
  }

  // Fallback: provincia cuando no vino locality ni sublocality.
  if (!details.comuna) {
    const nivel2 = componentes.find((c) =>
      c.types.includes('administrative_area_level_2')
    );
    if (nivel2?.longText) {
      details.comuna = nivel2.longText;
    }
  }

  return details;
}
